import { Link, useLocation, useParams } from 'react-router-dom';
import { cellarLabel } from '../auth/cellar-list';
import { useActiveCellar } from '../auth/CellarListContext';
import { useCellarStore } from '../store/CellarStoreContext';
import { CellarMetricsBar } from '../components/CellarMetricsBar';
import { DeleteCellarControl } from '../components/DeleteCellarControl';
import { ExportCellarButton } from '../components/ExportCellarButton';
import { emptyCellar, wineStock } from '../domain/cellar';
import { bottleCount, t, tf } from '../i18n';
import './HomePage.css';

/**
 * /c/:cellarId, the cellar's home page (owner requests, 2026-10-01 and 2026-10-06): the cellar's
 * name is in the top bar only (here a heading for screen readers), then two buttons side by side,
 * "+ Ajouter une bouteille" and "− Boire et noter" (its own page; renamed 2026-10-09), then the key figures, each
 * opening "Stock" with its filter. While the owner is inside someone else's cellar, "Exporter
 * cette cave en CSV" (T097) and "Supprimer cette cave" (FR-036, T074) sit at the bottom.
 */
export function HomePage() {
  const { cellarId } = useParams<{ cellarId: string }>();
  const { cellar, apply } = useCellarStore();
  const active = useActiveCellar();
  const state = useLocation().state as { drunk?: string; bottles?: number } | null;
  const drunk = state?.drunk;
  const bottles = state?.bottles ?? 1;

  if (!cellar) {
    return (
      <main>
        <p>{t('common.loading')}</p>
      </main>
    );
  }

  const base = `/c/${cellarId}`;
  const inStock = cellar.wines.some((wine) => wineStock(wine) > 0);

  return (
    <main className="home">
      <h1 className="visually-hidden">{cellarLabel(active)}</h1>
      {drunk && (
        <p role="status">
          {bottles > 1
            ? tf('home.drunkMany', { bottles: bottleCount(bottles), wine: drunk })
            : tf('home.drunk', { wine: drunk })}
        </p>
      )}

      <div className="home-actions">
        <Link to={`${base}/achat/nouveau`} className="cta-action home-action">
          <span aria-hidden="true">+</span> {t('inventory.addPurchase')}
        </Link>
        {inStock && (
          <Link to={`${base}/boire`} className="cta-action home-action">
            <span aria-hidden="true">−</span> {t('home.reduce')}
          </Link>
        )}
      </div>

      <CellarMetricsBar cellar={cellar} base={base} />

      {cellar.wines.length === 0 && (
        <p>{active.own ? t('inventory.empty') : t('inventory.emptyOther')}</p>
      )}

      {!active.own && (
        <ExportCellarButton
          label={t('export.other')}
          load={async () => ({ cellar, name: active.name })}
        />
      )}

      {!active.own && (
        <DeleteCellarControl
          onDelete={async () => {
            await apply(emptyCellar);
            return 'emptied';
          }}
        />
      )}
    </main>
  );
}
